import { formatDateTime } from '@kk/shared/utils/date';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cn } from 'cn';
import { useEffect, useRef, useState } from 'react';
import type { NotificationType } from '#ui/constants';
import { useTheme } from '#ui/hooks/useTheme.js';
import { LinkButton } from '../Button';
import { Icon, type IconType } from '../Icon';
import {
  removeToast,
  showToast,
  type ToastNotificationProps,
} from './private/state';

type ToastAppearance = {
  backgroundClassName: string;
  textClassName: string;
  icon: IconType | undefined;
  iconClassName: string;
  linkClassName: string;
  ariaType: 'foreground' | 'background';
};

interface ToastElementProps {
  toast: ToastNotificationProps;
  toastKey: string;
}

const toastAppearance: Record<NotificationType, ToastAppearance> = {
  default: {
    backgroundClassName: 'bg-neutral-5',
    textClassName: 'text-white',
    icon: undefined,
    iconClassName: '',
    linkClassName: 'text-white! underline',
    ariaType: 'background',
  },
  info: {
    backgroundClassName: 'bg-blue-60',
    textClassName: 'text-white',
    icon: 'info',
    iconClassName: 'text-white',
    linkClassName: 'text-white! underline',
    ariaType: 'background',
  },
  success: {
    backgroundClassName: 'bg-green-100',
    textClassName: 'text-white',
    icon: 'check-circle',
    iconClassName: 'text-white',
    linkClassName: 'text-white! underline',
    ariaType: 'foreground',
  },
  warning: {
    backgroundClassName: 'bg-orange-60',
    textClassName: '',
    icon: 'alert-triangle',
    iconClassName: 'text-orange-110',
    linkClassName: 'text-black! underline',
    ariaType: 'foreground',
  },
  danger: {
    backgroundClassName: 'bg-red-100',
    textClassName: 'text-white',
    icon: 'alert-diamond',
    iconClassName: 'text-white',
    linkClassName: 'text-white! underline',
    ariaType: 'foreground',
  },
};

function isToastClosable({ closable }: ToastNotificationProps): boolean {
  return closable ?? true;
}

function shouldAutoDismiss({
  autoDismiss,
  type,
  link,
}: ToastNotificationProps): boolean {
  return autoDismiss ?? (type !== 'danger' && link === undefined);
}

function animateToastRemoval(toastKey: string): void {
  const element = document.getElementById(toastKey);

  if (element && element.getAnimations().length === 0) {
    const swiped = element.dataset.swipe === 'end';
    const removeAnimation = element.animate(
      swiped
        ? [
            {
              transform: 'translateX(var(--radix-toast-swipe-end-x, 0px))',
            },
            {
              transform:
                'translateX(calc(100% + var(--radix-toast-swipe-end-x, 0px)))',
            },
          ]
        : [{ opacity: 1 }, { opacity: 0 }],
      {
        duration: swiped ? 100 : 400,
        easing: swiped ? 'ease-out' : 'cubic-bezier(0.6, 0, 0.4, 1)',
        fill: 'forwards',
        delay: swiped ? 0 : 100,
      },
    );
    removeAnimation.onfinish = () => {
      removeToast(toastKey);
    };
  }
}

function useToastAnimation(toastKey: string, autoDismiss: boolean): boolean {
  const [closed, setClosed] = useState(false);
  const animated = useRef(false);

  useEffect(() => {
    if (animated.current) return;
    animated.current = true;

    const element = document.getElementById(toastKey);
    if (!element) return;

    const list = document.querySelector('[data-toast-list]');
    if (list !== null) {
      list.animate(
        [
          { transform: `translateY(${element.offsetHeight}px)` },
          { transform: 'translateY(0px)' },
        ],
        {
          duration: 160,
          easing: 'cubic-bezier(0.6, 0, 0.4, 1)',
          fill: 'forwards',
        },
      );
    }
  }, [toastKey]);

  useEffect(() => {
    if (!autoDismiss) return;

    const timeout = window.setTimeout(() => {
      setClosed(true);
    }, 5000);
    return () => window.clearTimeout(timeout);
  }, [autoDismiss, toastKey]);

  return closed;
}

export function useToast(): {
  showToast: typeof showToast;
  removeToast: typeof removeToast;
} {
  return { showToast, removeToast };
}

export function ToastElement({ toast, toastKey }: ToastElementProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { headline, message, size, link, date } = toast;
  const isKunde = useTheme() === 'kunde';
  const closable = isToastClosable(toast);
  const autoDismiss = shouldAutoDismiss(toast);
  const closed = useToastAnimation(toastKey, autoDismiss && !isHovered);
  const appearance = toastAppearance[toast.type];

  const { key, closable: _, autoDismiss: __, ...toastProps } = toast;

  return (
    <ToastPrimitive.Root
      data-testid={`toast-${toastKey}`}
      id={toastKey}
      className={cn(
        'group transition-opacity ease-[cubic-bezier(0.6,0,0.4,1)]',
        'data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x)',
        'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform data-[swipe=cancel]:duration-200 data-[swipe=cancel]:ease-out',
        closed ? 'opacity-0 duration-400' : 'opacity-100',
      )}
      onTransitionEnd={(event) => {
        if (closed && event.target === event.currentTarget) {
          removeToast(toastKey);
        }
      }}
      open={true}
      duration={0}
      {...toastProps}
      type={appearance.ariaType}
      onOpenChange={(open) => {
        if (!open) animateToastRemoval(toastKey);
      }}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      <div
        className={cn(
          'w-78 select-text p-2 text-xs',
          appearance.textClassName,
          'laane:rounded',
          'kunde:shadow-md kunde:rounded-md',
          appearance.backgroundClassName,
          { 'kunde:min-w-105': size === 'long' },
        )}
      >
        <div className="relative flex w-full flex-row">
          <Icon
            type={appearance.icon}
            className={appearance.iconClassName}
            size={isKunde ? 'medium' : 'small'}
            solid
          />
          <div
            className={cn(
              'flex w-full flex-row justify-between pl-2',
              autoDismiss && 'pr-10',
            )}
          >
            <div className="space-y-1">
              <ToastPrimitive.Title className="kunde:text-sm font-semibold">
                {headline}
              </ToastPrimitive.Title>

              <ToastPrimitive.Description asChild>
                <div className="flex flex-col font-normal">
                  {size === 'long' || message ? (
                    <span className="pb-1">{message}</span>
                  ) : null}
                  {link ? (
                    <LinkButton
                      className={appearance.linkClassName}
                      {...link}
                    />
                  ) : null}
                  {size === 'long' && date ? (
                    <time className="text-2xs pt-4">
                      {formatDateTime(date)}
                    </time>
                  ) : null}
                </div>
              </ToastPrimitive.Description>
            </div>
            <div
              className={cn(
                autoDismiss && 'absolute right-0 top-1/2 -translate-y-1/2',
              )}
            >
              {closable ? (
                <ToastPrimitive.Close
                  aria-label="Luk"
                  className={cn(
                    autoDismiss &&
                      'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100',
                  )}
                >
                  <Icon
                    type="cross"
                    size={isKunde ? 'small' : 'tiny'}
                    className="kunde:m-2"
                  />
                </ToastPrimitive.Close>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </ToastPrimitive.Root>
  );
}
